import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import CtaBand from "@/sections/CtaBand";
import { ARTICLES, CATEGORIES, CATEGORY_GUIDE } from "@/data/articles";
import { slugify } from "@/data/site";

const Conteudos = () => {
  const [filter, setFilter] = useState("Todos");
  const [query, setQuery] = useState("");
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ARTICLES.filter((a) => {
      const inCategory = filter === "Todos" || a.category === filter;
      const inQuery =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q);
      return inCategory && inQuery;
    });
  }, [filter, query]);

  return (
    <>
      <SEO
        title="Para compreender melhor — Conteúdos sobre Psicologia e Neuropsicologia | Sabrina Mota"
        description="Artigos sobre Psicologia, Neuropsicologia, infância, adolescência, TDAH, TEA, ansiedade e aprendizagem, escritos pela psicóloga Sabrina Mota."
        path="/conteudos"
      />
      <section className="pt-28 lg:pt-40" data-testid="conteudos-page">
        <div className="container-x pb-20 lg:pb-28">
          <FadeUp>
            <p className="eyebrow" data-testid="conteudos-eyebrow">Conteúdos</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="conteudos-title">
              Para <em className="italic text-raiz-700">compreender melhor</em>
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="lead mt-7 max-w-2xl">
              Informação também pode ser um primeiro passo para compreender melhor uma dúvida ou uma demanda.
            </p>
          </FadeUp>

          <div className="mt-16 lg:mt-20" data-testid="temas-guia">
            <FadeUp delay={0.18}>
              <p className="eyebrow">Entenda os temas</p>
              <h2 className="mt-4 max-w-2xl font-serif text-2xl font-light leading-snug text-ink sm:text-3xl">
                O que significa cada um desses assuntos?
              </h2>
            </FadeUp>
            <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" gap={0.08}>
              {CATEGORY_GUIDE.map((g) => {
                const count = ARTICLES.filter((a) => a.category === g.category).length;
                return (
                  <StaggerItem key={g.category} className="h-full">
                    <button
                      type="button"
                      data-testid={`tema-${slugify(g.category)}`}
                      onClick={() => {
                        setFilter(g.category);
                        document.getElementById("lista-artigos")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className="group flex h-full w-full flex-col rounded-3xl border border-raiz-900/10 bg-cream p-7 text-left transition-[transform,border-color,background-color] duration-500 hover:-translate-y-1 hover:border-gold-400/60 hover:bg-gold-100/30"
                    >
                      <span className="flex items-center gap-3">
                        <span className="h-px w-6 bg-gold-500 transition-all duration-500 group-hover:w-9" aria-hidden="true" />
                        <span className="font-serif text-xl text-ink">{g.category}</span>
                      </span>
                      <span className="mt-4 flex-1 text-sm leading-relaxed text-ink/65">{g.text}</span>
                      <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-raiz-600">
                        {count > 0 ? `Ver ${count === 1 ? "conteúdo" : "conteúdos"}` : "Ver tema"}
                        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </button>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>

          <FadeUp delay={0.18} className="mt-16 max-w-md lg:mt-20">
            <label htmlFor="busca" className="sr-only">Buscar conteúdo</label>
            <input
              id="busca"
              type="search"
              data-testid="conteudos-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por tema, título ou palavra-chave…"
              className="w-full rounded-full border border-raiz-800/20 bg-cream px-6 py-3.5 text-sm text-ink placeholder:text-ink/35 transition-colors duration-300 focus:border-raiz-700 focus:outline-none"
            />
          </FadeUp>

          <FadeUp delay={0.2} className="mt-6 flex flex-wrap gap-2.5">
            {["Todos", ...CATEGORIES].map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`filter-${slugify(c)}`}
                aria-pressed={filter === c}
                onClick={() => setFilter(c)}
                className={`rounded-full border px-4 py-2 text-[13px] tracking-wide transition-all duration-300 ${
                  filter === c
                    ? "border-raiz-800 bg-raiz-800 text-paper"
                    : "border-raiz-800/20 text-raiz-900 hover:border-raiz-800"
                }`}
              >
                {c}
              </button>
            ))}
          </FadeUp>

          <span id="lista-artigos" className="block scroll-mt-32" aria-hidden="true" />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" gap={0.1}>
            {list.length === 0 && (
              <p className="col-span-full py-10 text-center text-sm text-ink/50" data-testid="conteudos-empty">
                Nenhum conteúdo encontrado para essa busca.
              </p>
            )}
            {list.map((a) => (
              <StaggerItem key={a.slug} className="h-full">
                <Link
                  to={`/conteudos/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-raiz-900/10 bg-cream p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-raiz-600/50 hover:bg-raiz-50"
                >
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-raiz-600">{a.category}</p>
                    <p className="text-xs text-ink/40">{a.minutes} min</p>
                  </div>
                  <h2 className="mt-4 font-serif text-xl font-normal leading-snug text-ink lg:text-2xl">{a.title}</h2>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/60">{a.excerpt}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm text-raiz-700">
                    Ler conteúdo
                    <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <CtaBand />
    </>
  );
};

export default Conteudos;
